import {
  FaArrowRight,
  FaCalendarAlt,
  FaFileAlt,
  FaNewspaper,
} from "react-icons/fa";

import aktuelltData from "./aktuelltData";

function Eyebrow({ children }) {
  return (
    <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#3BAFA3] sm:text-xs sm:tracking-[0.18em]">
      {children}
    </p>
  );
}

function NewsItem({ nyhet }) {
  const viktig = nyhet.prioritet === "viktig";

  const ärPdf =
    typeof nyhet.url === "string" &&
    nyhet.url.toLowerCase().split("?")[0].endsWith(".pdf");

  return (
    <article
      className={`group relative border-t py-6 sm:py-8 ${
        viktig
          ? "border-[#3BAFA3]/60"
          : "border-neutral-200"
      }`}
    >
      <div
        className="
          grid
          gap-5
          sm:gap-6
          md:grid-cols-[1fr_auto]
          md:items-center
        "
      >
        {/* NYHETENS INNEHÅLL */}
        <div className="min-w-0">
          {/* KATEGORI OCH DATUM */}
          <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2">
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.08em] text-[#3BAFA3]">
              {ärPdf ? (
                <FaFileAlt size={12} />
              ) : (
                <FaNewspaper size={12} />
              )}

              {nyhet.typ || "Nyhet"}
            </span>

            {nyhet.datum && (
              <span className="inline-flex items-center gap-2 text-xs text-neutral-400 sm:text-sm">
                <FaCalendarAlt size={11} />
                {nyhet.datum}
              </span>
            )}

            {viktig && (
              <span className="rounded-full bg-[#E8F7F5] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-[#278F85]">
                Viktigt
              </span>
            )}
          </div>

          {/* RUBRIK */}
          <h3
            className="
              max-w-3xl
              text-xl
              font-semibold
              leading-snug
              tracking-[-0.035em]
              text-neutral-900
              transition-colors
              duration-300
              group-hover:text-[#278F85]
              sm:text-2xl
              md:text-[28px]
              md:leading-tight
            "
          >
            {nyhet.titel}
          </h3>

          {/* BESKRIVNING */}
          {nyhet.beskrivning && (
            <p className="mt-3 max-w-2xl text-sm leading-7 text-neutral-500 sm:mt-4 sm:text-[15px] sm:leading-8">
              {nyhet.beskrivning}
            </p>
          )}
        </div>

        {/* LÄNK */}
        <div className="md:pl-6">
          <a
            href={nyhet.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${nyhet.knappText || (ärPdf ? "Öppna dokumentet" : "Läs mer")}: ${nyhet.titel}`}
            className="
              group/link
              inline-flex
              min-h-11
              items-center
              gap-3
              text-sm
              font-semibold
              text-neutral-900
              transition-colors
              duration-300
              hover:text-[#3BAFA3]
              focus:outline-none
              focus-visible:rounded
              focus-visible:ring-2
              focus-visible:ring-[#3BAFA3]
              focus-visible:ring-offset-4
            "
          >
            <span>
              {nyhet.knappText ||
                (ärPdf ? "Öppna dokumentet" : "Läs mer")}
            </span>

            <FaArrowRight
              size={11}
              className="
                text-[#3BAFA3]
                transition-transform
                duration-300
                group-hover/link:translate-x-1
              "
            />
          </a>
        </div>
      </div>
    </article>
  );
}

export default function Aktuellt() {
  const nyheter = aktuelltData
    .filter((nyhet) => nyhet.aktiv && nyhet.url)
    .sort((a, b) => {
      if (a.prioritet === "viktig" && b.prioritet !== "viktig") {
        return -1;
      }

      if (a.prioritet !== "viktig" && b.prioritet === "viktig") {
        return 1;
      }

      return 0;
    });

  if (nyheter.length === 0) return null;

  return (
    <section
      id="nyheter"
      className="scroll-mt-20 bg-white"
    >
      <div
        className="
          mx-auto
          max-w-6xl
          px-4
          py-16
          sm:px-6
          sm:py-20
          md:px-10
          md:py-24
          lg:px-16
          xl:py-28
        "
      >
        {/* INTRODUKTION */}
        <div
          className="
            grid
            gap-5
            md:gap-8
            lg:grid-cols-[0.8fr_1.2fr]
            lg:gap-20
          "
        >
          <div>
            <Eyebrow>Aktuellt från oss</Eyebrow>

            <h2
              className="
                mt-4
                text-[36px]
                font-semibold
                leading-[1.02]
                tracking-[-0.05em]
                text-neutral-900
                sm:mt-5
                sm:text-5xl
                md:text-6xl
              "
            >
              Nyheter
              <span className="text-[#3BAFA3]">.</span>
            </h2>
          </div>

          <div className="max-w-2xl lg:pt-8">
            <p className="text-base leading-7 text-neutral-600 sm:text-lg sm:leading-8">
              Här samlar vi aktuella nyheter, viktig information och
              uppdateringar från Räkna med mig.
            </p>

            <p className="mt-3 text-sm leading-7 text-neutral-500 sm:mt-4 sm:text-[15px] sm:leading-8">
              Ta del av det senaste och hitta vidare till information
              och dokument som kan vara bra att känna till.
            </p>
          </div>
        </div>

        {/* NYHETSLISTA */}
        <div className="mt-10 sm:mt-14 md:mt-16">
          <div className="border-t border-neutral-200">
            {nyheter.map((nyhet) => (
              <NewsItem key={nyhet.id} nyhet={nyhet} />
            ))}

            <div className="border-t border-neutral-200" />
          </div>
        </div>

        {/* AVSLUTANDE RAD */}
        <div className="mt-6 flex items-start gap-3 sm:mt-8">
          <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-[#3BAFA3]" />

          <p className="text-sm leading-7 text-neutral-500">
            Vi uppdaterar sidan när det finns ny information att dela.
          </p>
        </div>
      </div>
    </section>
  );
}